import { type ReactNode } from "react"
import { Link, NavLink, useNavigate } from "react-router-dom"
import { useQueryClient, useMutation } from "@tanstack/react-query"
import {
  LayoutDashboard,
  GitBranch,
  Briefcase,
  Users,
  CheckSquare,
  FileText,
  Trophy,
  Zap,
  Mail,
  BarChart3,
  BookOpen,
  User,
  Settings,
  ScrollText,
  Plus,
  LogOut,
  ChevronDown,
  Inbox,
} from "lucide-react"
import { authApi } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import { cn, getInitials } from "@/lib/utils"
import { GlobalSearch } from "@/components/GlobalSearch"
import { NotificationsBell } from "@/components/NotificationsBell"
import { useQuery } from "@tanstack/react-query"
import { integrationsApi } from "@/lib/api"
import { useOutlookAutoSync } from "@/hooks/useOutlookAutoSync"
import { Button } from "@/components/ui/button"

type NavItem = {
  to: string
  label: string
  icon: typeof LayoutDashboard
  adminOnly?: boolean
}

const NAV_MAIN: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Inbox },
  { to: "/pipeline", label: "Pipeline", icon: GitBranch },
  { to: "/deals", label: "Deals", icon: Briefcase },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/tasks", label: "Tasks & Follow-ups", icon: CheckSquare },
  { to: "/documents", label: "Documents", icon: FileText },
  // Lenders directory hidden — lender logos still power the deal selector
  // + dashboard, but the admin management page is not part of the nav.
  // { to: "/lenders", label: "Lenders", icon: Building2 },
  { to: "/ambition", label: "Ambition", icon: Trophy },
  { to: "/automations", label: "Automations", icon: Zap },
  { to: "/templates", label: "Templates", icon: Mail },
  { to: "/reports", label: "Reports", icon: BarChart3 },
]

const NAV_SECONDARY: NavItem[] = [
  { to: "/manual", label: "User Manual", icon: BookOpen },
  { to: "/my-settings", label: "My Settings", icon: User },
  { to: "/users", label: "Users", icon: Users, adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, adminOnly: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText, adminOnly: true },
]

export type AppShellProps = {
  children: ReactNode
  title?: string
  subtitle?: string
  actions?: ReactNode
  greetingUser?: boolean
}

export function AppShell({ children, title, subtitle, actions, greetingUser }: AppShellProps) {
  // Silently poll Outlook every ~2 minutes while the tab is focused, but
  // only if the current user has actually connected Outlook. Uses the same
  // /integrations/status query as the rest of the app so it's cached.
  const status = useQuery({
    queryKey: ["integration-status"],
    queryFn: () => integrationsApi.status(),
    staleTime: 60_000,
  })
  const outlookConnected = status.data?.user.outlook?.isActive === true
  useOutlookAutoSync({ enabled: outlookConnected })

  return (
    <div className="bg-rc-grey-50 flex min-h-screen">
      <Sidebar />
      <div className="ml-[260px] flex min-w-0 flex-1 flex-col">
        <Topbar title={title} subtitle={subtitle} actions={actions} greetingUser={greetingUser} />
        <main className="flex-1 overflow-x-auto p-6 lg:p-8">{children}</main>
      </div>
    </div>
  )
}

// ============================================================
// Sidebar
// ============================================================
function Sidebar() {
  const { user } = useAuth()
  const isAdmin = user?.role === "admin"

  return (
    <aside className="rc-sidebar border-rc-navy-800 bg-rc-navy-brand text-rc-navy-100 fixed left-0 top-0 z-30 flex h-screen w-[260px] flex-col border-r">
      {/* Brand — uses dark-mode variant for the navy sidebar */}
      <div className="flex h-20 items-center justify-center px-4">
        <img
          src="/brand/rivercity-logo-dark.png"
          srcSet="/brand/rivercity-logo-dark.png 1x, /brand/rivercity-logo-dark@2x.png 2x"
          alt="Rivercity Finance"
          className="h-14 w-auto"
        />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="mb-4">
          <div className="text-rc-navy-400 px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider">
            Main
          </div>
          {NAV_MAIN.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
        </div>
        <div>
          <div className="text-rc-navy-400 px-3 pb-2 pt-2 text-[10px] font-semibold uppercase tracking-wider">
            System
          </div>
          {NAV_SECONDARY.filter((i) => !i.adminOnly || isAdmin).map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
        </div>
      </nav>

      {/* User footer */}
      <SidebarUserFooter />
    </aside>
  )
}

function SidebarLink({ item }: { item: NavItem }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        cn(
          "mb-0.5 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-rc-blue-600 text-white shadow-sm"
            : "text-rc-navy-200 hover:bg-rc-navy-800 hover:text-white"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  )
}

function SidebarUserFooter() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const logout = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: async () => {
      qc.clear()
      navigate("/login", { replace: true })
    },
  })

  if (!user) return null

  return (
    <div className="border-rc-navy-800 border-t p-3">
      <div className="flex items-center gap-3 rounded-md p-2">
        <div className="bg-rc-blue-600 grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold text-white">
          {getInitials(user.fullName)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-white">{user.fullName}</div>
          <div className="text-rc-navy-300 truncate text-[11px] capitalize">{user.role}</div>
        </div>
        <button
          onClick={() => logout.mutate()}
          className="text-rc-navy-300 hover:bg-rc-navy-800 rounded-md p-1.5 hover:text-white"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// ============================================================
// Topbar
// ============================================================
type TopbarProps = {
  title?: string
  subtitle?: string
  actions?: ReactNode
  greetingUser?: boolean
}

function Topbar({ title, subtitle, actions, greetingUser }: TopbarProps) {
  const { user } = useAuth()

  return (
    <header className="border-border sticky top-0 z-20 flex h-16 shrink-0 items-center gap-4 border-b bg-white px-6">
      {/* Title / greeting */}
      <div className="min-w-0 flex-shrink-0 max-w-[280px] lg:max-w-[340px]">
        {greetingUser && user ? (
          <div className="min-w-0">
            <h1 className="text-foreground truncate text-xl font-semibold">
              Good {getTimeOfDay()}, {user.fullName.split(" ")[0]} 👋
            </h1>
            {subtitle ? (
              <p className="text-muted-foreground truncate text-sm">{subtitle}</p>
            ) : null}
          </div>
        ) : title ? (
          <div className="min-w-0">
            <h1 className="text-foreground truncate text-xl font-semibold">{title}</h1>
            {subtitle ? (
              <p className="text-muted-foreground truncate text-sm">{subtitle}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Global search */}
      <div className="hidden flex-1 justify-center lg:flex">
        <GlobalSearch />
      </div>

      {/* Notifications */}
      <div className="shrink-0">
        <NotificationsBell />
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center">
      {actions ?? (
        <Button asChild>
          <Link to="/deals/new">
            <Plus className="h-4 w-4" />
            New Deal
          </Link>
        </Button>
      )}
      </div>
    </header>
  )
}

function getTimeOfDay(): string {
  const h = new Date().getHours()
  if (h < 12) return "morning"
  if (h < 18) return "afternoon"
  return "evening"
}

// Suppress unused warning while ChevronDown is planned for later phases
void ChevronDown
